import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Button, Input, Label, Table, Modal, ModalHeader, ModalBody } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import DocumentosContenedor from "./DocumentosContenedor";
import RevalidacionModal from "./RevalidacionModal";
import { fmtNum, headersAuth } from "./revalidacionUtils";

const URL_PAGOS = `${process.env.REACT_APP_URL_REVALIDACIONES}PagosNaviera`;
const URL_REV = process.env.REACT_APP_URL_REVALIDACIONES_CONTENEDOR;

const fmtMoneda = (v, moneda) => `${moneda || "USD"} $${fmtNum(v, 2)}`;

// Pagos a naviera por hacer: contenedor, monto y comprobante para quien paga (en lugar de pedirlos por WhatsApp)
export default function PagosNaviera() {
  const { user } = useContext(AuthContext);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [todos, setTodos] = useState(false);
  const [fNaviera, setFNaviera] = useState("");
  const [comprobante, setComprobante] = useState(null); // fila
  const [abierto, setAbierto] = useState(null);

  const puedeEditar = !!(user.contenedoresEdit || user.contenedoresCreate);

  const carga = () => {
    setCargando(true);
    axios
      .get(URL_PAGOS + (todos ? "?todos=1" : ""), { headers: headersAuth() })
      .then(({ data }) => setFilas(Array.isArray(data) ? data : []))
      .catch(() => setFilas([]))
      .finally(() => setCargando(false));
  };
  useEffect(carga, [todos]); // eslint-disable-line

  const navieras = [...new Set(filas.map((f) => f.contenedor.naviera).filter(Boolean))].sort();
  const visibles = useMemo(() => filas.filter((f) => !fNaviera || f.contenedor.naviera === fNaviera), [filas, fNaviera]);

  const totales = useMemo(() => {
    const t = { USD: 0, MXN: 0, sinComprobante: 0 };
    visibles.forEach((f) => {
      if (f.revalidacion.pagoConfirmado) return;
      t[f.revalidacion.monedaPagoNaviera || "USD"] += Number(f.revalidacion.montoPagoNaviera) || 0;
      if (f.contenedor.docs.pagoNaviera !== "Si") t.sinComprobante++;
    });
    return t;
  }, [visibles]);

  const confirmarPago = async (f) => {
    const { value } = await Swal.fire({
      title: `Confirmar pago de ${f.contenedor.referencia}`,
      html: `${f.contenedor.naviera} · ${fmtMoneda(f.revalidacion.montoPagoNaviera, f.revalidacion.monedaPagoNaviera)}`,
      input: "date",
      inputValue: moment().format("YYYY-MM-DD"),
      showCancelButton: true,
      confirmButtonText: "Confirmar pago",
      cancelButtonText: "Cancelar",
    });
    if (!value) return;
    try {
      await axios.put(`${URL_REV}/${f.contenedor._id}`, { pagoConfirmado: value }, { headers: headersAuth() });
      Swal.fire({ icon: "success", title: "Pago confirmado", timer: 1200, showConfirmButton: false });
      carga();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.message || err.message });
    }
  };

  const excel = () => {
    const datos = visibles.map((f) => ({
      Contenedor: f.contenedor.referencia,
      Referencia: f.contenedor.numeroReferencia,
      Cliente: f.contenedor.cliente,
      Naviera: f.contenedor.naviera,
      ETA: f.contenedor.eta,
      Solicitud: f.revalidacion.solicitudPago,
      Monto: Number(f.revalidacion.montoPagoNaviera) || 0,
      Moneda: f.revalidacion.monedaPagoNaviera || "USD",
      Comprobante: f.contenedor.docs.pagoNaviera === "Si" ? "Si" : "No",
      PagoConfirmado: f.revalidacion.pagoConfirmado || "",
    }));
    const cols = Object.keys(datos[0] || { Contenedor: "" });
    new ExportJsonExcel({ fileName: "PagosNaviera", datas: [{ sheetData: datos, sheetName: "Pagos", sheetFilter: cols, sheetHeader: cols }] }).saveExcel();
  };

  if (!user.menuContenedores) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-sm-11 rc-page">
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <span className="page-title-icon"><i className="fas fa-money-check-alt" /></span>
              Pagos a naviera
            </h3>
            <p className="page-subtitle">Pagos solicitados a las navieras pendientes de confirmar, con monto y comprobante</p>
          </div>
          <div className="page-actions">
            <Button size="sm" color="secondary" href="/Revalidaciones"><i className="fas fa-arrow-left" /> Revalidaciones</Button>
            <Button size="sm" onClick={excel} disabled={!visibles.length}><i className="far fa-file-excel" /> Excel</Button>
          </div>
        </div>

        <div className="filter-bar">
          <div>
            <Label>Naviera</Label>
            <Input bsSize="sm" type="select" value={fNaviera} onChange={(e) => setFNaviera(e.target.value)}>
              <option value="">Todas</option>
              {navieras.map((x) => <option key={x}>{x}</option>)}
            </Input>
          </div>
          <div style={{ justifyContent: "flex-end" }}>
            <Label check style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} /> Incluir pagos ya confirmados
            </Label>
          </div>
        </div>

        <div className={`rc-kpis rv-kpis${cargando ? " rc-loading" : ""}`}>
          {[
            { label: "Pagos por hacer", valor: fmtNum(visibles.filter((f) => !f.revalidacion.pagoConfirmado).length), icon: "fas fa-list-ol", bg: "#dbeafe", fg: "#1d4ed8" },
            { label: "Total USD", valor: `$${fmtNum(totales.USD, 2)}`, icon: "fas fa-dollar-sign", bg: "#dcfce7", fg: "#15803d" },
            { label: "Total MXN", valor: `$${fmtNum(totales.MXN, 2)}`, icon: "fas fa-coins", bg: "#fef3c7", fg: "#b45309" },
            { label: "Sin comprobante", valor: fmtNum(totales.sinComprobante), icon: "fas fa-file-upload", bg: "#fee2e2", fg: "#b91c1c" },
          ].map((k) => (
            <div key={k.label} className="rc-kpi">
              <div className="rc-kpi-icon" style={{ background: k.bg, color: k.fg }}><i className={k.icon} /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{k.label}</div>
                <div className="rc-kpi-value">{k.valor}</div>
              </div>
            </div>
          ))}
        </div>

        {cargando ? (
          <div className="rc-state"><i className="fas fa-spinner fa-spin" /><div>Cargando pagos…</div></div>
        ) : visibles.length === 0 ? (
          <div className="rc-state">
            <i className="fas fa-check-circle" />
            <div>No hay pagos pendientes. Se agregan al capturar la "Solicitud de pago a naviera" en la revalidación.</div>
          </div>
        ) : (
          <Table size="sm" hover className="table-responsive-xl">
            <thead>
              <tr>
                <th>Contenedor</th><th>Referencia</th><th>Cliente</th><th>Naviera</th><th>ETA</th>
                <th>Solicitud</th><th className="td-num">Monto</th><th>Comprobante</th><th>Pago</th><th></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f) => {
                const r = f.revalidacion;
                const dias = r.solicitudPago ? moment().diff(moment(r.solicitudPago, "YYYY-MM-DD"), "days") : 0;
                return (
                  <tr key={f.contenedor._id}>
                    <td className="td-strong">{f.contenedor.referencia}</td>
                    <td>{f.contenedor.numeroReferencia}</td>
                    <td>{f.contenedor.cliente}</td>
                    <td>{f.contenedor.naviera}</td>
                    <td>{f.contenedor.eta || "—"}</td>
                    <td>
                      {r.solicitudPago}
                      {!r.pagoConfirmado && dias > 0 ? <div className="rv-sub">hace {dias} d</div> : null}
                    </td>
                    <td className="td-num td-money">
                      {Number(r.montoPagoNaviera) ? fmtMoneda(r.montoPagoNaviera, r.monedaPagoNaviera) : <span className="td-muted">Sin monto</span>}
                    </td>
                    <td>
                      <button type="button" className={`badge-soft badge-soft-${f.contenedor.docs.pagoNaviera === "Si" ? "success" : "warning"} rv-badge-btn`} onClick={() => setComprobante(f)}>
                        {f.contenedor.docs.pagoNaviera === "Si" ? "Ver / subir" : "Subir"}
                      </button>
                    </td>
                    <td>
                      {r.pagoConfirmado ? (
                        <span className="badge-soft badge-soft-success">{r.pagoConfirmado}</span>
                      ) : puedeEditar ? (
                        <Button size="sm" color="success" onClick={() => confirmarPago(f)}><i className="fas fa-check" /> Confirmar</Button>
                      ) : (
                        <span className="badge-soft badge-soft-warning">Pendiente</span>
                      )}
                    </td>
                    <td>
                      <Button size="sm" color="info" title="Revalidación" onClick={() => setAbierto(f.contenedor._id)}><i className="fas fa-stamp" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </div>

      <Modal isOpen={!!comprobante} toggle={() => setComprobante(null)}>
        <ModalHeader toggle={() => setComprobante(null)}>
          Comprobante de pago · {comprobante ? comprobante.contenedor.referencia : ""}
        </ModalHeader>
        <ModalBody>
          {comprobante ? (
            <DocumentosContenedor
              contenedorId={comprobante.contenedor._id}
              docs={comprobante.contenedor.docs}
              tipos={["pagoNaviera", "cargosLocales", "garantia"]}
              puedeSubir={!!user.contenedoresCreate}
              onChange={(docs) => {
                setComprobante((c) => ({ ...c, contenedor: { ...c.contenedor, docs } }));
                setFilas((fs) => fs.map((x) => (x.contenedor._id === comprobante.contenedor._id ? { ...x, contenedor: { ...x.contenedor, docs } } : x)));
              }}
            />
          ) : null}
        </ModalBody>
      </Modal>

      <RevalidacionModal
        isOpen={!!abierto}
        toggle={() => setAbierto(null)}
        contenedorId={abierto}
        modo={user.menuAdmin ? "cobros" : "datos"}
        puedeEditar={puedeEditar}
        puedeSubir={!!user.contenedoresCreate}
        onSaved={carga}
      />
    </>
  );
}
